import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Globe, FileText, Search, Tag } from 'lucide-react';
import { adminGetSettings, adminUpdateSettings, getServices } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

export default function AdminSEO() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [globalSeo, setGlobalSeo] = useState({
    site_title: '',
    site_description: '',
    site_keywords: '',
    og_image: '',
    google_analytics: '',
    facebook_pixel: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, servicesRes] = await Promise.all([
          adminGetSettings(),
          getServices()
        ]);
        
        const settingsMap = {};
        settingsRes.data.forEach(s => {
          settingsMap[s.key] = s.value;
        });
        
        setGlobalSeo({
          site_title: settingsMap.site_title || 'A&D Service - Bauarbeiten & Dienstleistungen',
          site_description: settingsMap.site_description || 'Professionelle Bauarbeiten, Renovierungen und Gebäudereinigung in Deutschland. Zuverlässig, schnell und sauber.',
          site_keywords: settingsMap.site_keywords || 'Bauarbeiten, Trockenbau, Malerarbeiten, Bodenverlegung, Gebäudereinigung, Entrümpelung, Abbrucharbeiten, Deutschland',
          og_image: settingsMap.og_image || '',
          google_analytics: settingsMap.google_analytics || '',
          facebook_pixel: settingsMap.facebook_pixel || '',
        });
        
        setServices(servicesRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Gabim gjatë marrjes së të dhënave');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveGlobalSeo = async () => {
    setIsSaving(true);
    try {
      const settingsArray = Object.entries(globalSeo).map(([key, value]) => ({
        key,
        value: value || '',
      }));
      await adminUpdateSettings(settingsArray);
      toast.success('Konfigurimet SEO u ruajtën!');
    } catch (err) {
      console.error('Error saving SEO settings:', err);
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-seo-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A]">SEO & Meta</h1>
        <p className="text-[#71717A]">Menaxho konfigurimet SEO për motorët e kërkimit</p>
      </div>

      <Tabs defaultValue="global" className="space-y-6">
        <TabsList className="bg-[#F4F4F5]">
          <TabsTrigger value="global" className="data-[state=active]:bg-white">
            <Globe size={16} className="mr-2" />
            Global SEO
          </TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-white">
            <FileText size={16} className="mr-2" />
            Shërbimet
          </TabsTrigger>
          <TabsTrigger value="tracking" className="data-[state=active]:bg-white">
            <Search size={16} className="mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Global SEO Tab */}
        <TabsContent value="global">
          <div className="bg-white p-8 rounded-sm border border-[#E4E4E7]">
            <h2 className="text-lg font-semibold text-[#0A0A0A] mb-6">Konfigurimet Globale SEO</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="site_title" className="flex items-center gap-2 mb-2">
                  <Tag size={16} className="text-[#D4B982]" />
                  Titulli i Faqes (Title Tag)
                </Label>
                <Input
                  id="site_title"
                  value={globalSeo.site_title}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, site_title: e.target.value })}
                  placeholder="A&D Service - Bauarbeiten & Dienstleistungen"
                  data-testid="seo-site-title"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  Rekomandohet: 50-60 karaktere. Aktualisht: {globalSeo.site_title.length} karaktere
                </p>
              </div>

              <div>
                <Label htmlFor="site_description" className="flex items-center gap-2 mb-2">
                  <FileText size={16} className="text-[#D4B982]" />
                  Meta Përshkrimi (Meta Description)
                </Label>
                <Textarea
                  id="site_description"
                  value={globalSeo.site_description}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, site_description: e.target.value })}
                  rows={3}
                  placeholder="Përshkrim i shkurtër i faqes..."
                  data-testid="seo-site-description"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  Rekomandohet: 150-160 karaktere. Aktualisht: {globalSeo.site_description.length} karaktere
                </p>
              </div>

              <div>
                <Label htmlFor="site_keywords" className="flex items-center gap-2 mb-2">
                  <Search size={16} className="text-[#D4B982]" />
                  Fjalë Kyçe (Keywords)
                </Label>
                <Textarea
                  id="site_keywords"
                  value={globalSeo.site_keywords}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, site_keywords: e.target.value })}
                  rows={2}
                  placeholder="bauarbeiten, trockenbau, malerarbeiten..."
                  data-testid="seo-site-keywords"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  Ndani fjalët kyçe me presje
                </p>
              </div>

              <div>
                <Label htmlFor="og_image" className="flex items-center gap-2 mb-2">
                  <Globe size={16} className="text-[#D4B982]" />
                  OG Image URL (Për rrjetet sociale)
                </Label>
                <Input
                  id="og_image"
                  value={globalSeo.og_image}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, og_image: e.target.value })}
                  placeholder="https://example.com/og-image.jpg"
                  data-testid="seo-og-image"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  Imazhi që shfaqet kur ndahet faqja në rrjete sociale (1200x630px rekomandohet)
                </p>
              </div>

              <div className="pt-4 border-t border-[#E4E4E7]">
                <Button
                  onClick={handleSaveGlobalSeo}
                  disabled={isSaving}
                  className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
                  data-testid="seo-save-global"
                >
                  {isSaving ? 'Duke ruajtur...' : (
                    <>
                      <Save size={18} className="mr-2" />
                      Ruaj Konfigurimet
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* SEO Preview */}
          <div className="mt-6 bg-white p-6 rounded-sm border border-[#E4E4E7]">
            <h3 className="text-sm font-semibold text-[#71717A] mb-4">Pamja në Google</h3>
            <div className="max-w-xl">
              <p className="text-[#1a0dab] text-lg hover:underline cursor-pointer">
                {globalSeo.site_title || 'Titulli i Faqes'}
              </p>
              <p className="text-[#006621] text-sm">
                https://adservice.de
              </p>
              <p className="text-[#545454] text-sm mt-1 line-clamp-2">
                {globalSeo.site_description || 'Meta përshkrimi do të shfaqet këtu...'}
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Services SEO Tab */}
        <TabsContent value="services">
          <div className="bg-white rounded-sm border border-[#E4E4E7]">
            <div className="p-6 border-b border-[#E4E4E7]">
              <h2 className="text-lg font-semibold text-[#0A0A0A]">SEO për Shërbimet</h2>
              <p className="text-sm text-[#71717A]">
                Konfiguro meta tags për secilën faqe shërbimi. Ndrysho SEO-n duke edituar shërbimin në faqen "Shërbimet".
              </p>
            </div>
            
            <div className="divide-y divide-[#E4E4E7]">
              {services.map((service) => (
                <div key={service.id} className="p-6 hover:bg-[#F4F4F5] transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-medium text-[#0A0A0A]">{service.title_de}</h3>
                      <p className="text-sm text-[#71717A] mt-1">/{service.slug}</p>
                      
                      <div className="mt-3 space-y-1 text-xs">
                        <p className="text-[#71717A]">
                          <span className="font-medium">Meta Title:</span>{' '}
                          <span className={service.meta_title ? 'text-green-600' : 'text-orange-500'}>
                            {service.meta_title || 'Jo i konfiguruar (do të përdoret titulli)'}
                          </span>
                        </p>
                        <p className="text-[#71717A]">
                          <span className="font-medium">Meta Description:</span>{' '}
                          <span className={service.meta_description ? 'text-green-600' : 'text-orange-500'}>
                            {service.meta_description ? `${service.meta_description.substring(0, 50)}...` : 'Jo i konfiguruar (do të përdoret përshkrimi i shkurtër)'}
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    <div className={`px-2 py-1 text-xs font-medium rounded ${
                      service.meta_title && service.meta_description 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {service.meta_title && service.meta_description ? 'SEO OK' : 'Nevojitet'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking">
          <div className="bg-white p-8 rounded-sm border border-[#E4E4E7]">
            <h2 className="text-lg font-semibold text-[#0A0A0A] mb-6">Analytics & Tracking</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="google_analytics" className="flex items-center gap-2 mb-2">
                  <Search size={16} className="text-[#D4B982]" />
                  Google Analytics ID
                </Label>
                <Input
                  id="google_analytics"
                  value={globalSeo.google_analytics}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, google_analytics: e.target.value })}
                  placeholder="G-XXXXXXXXXX ose UA-XXXXXXXX-X"
                  data-testid="seo-google-analytics"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  Measurement ID nga Google Analytics 4 ose Universal Analytics
                </p>
              </div>

              <div>
                <Label htmlFor="facebook_pixel" className="flex items-center gap-2 mb-2">
                  <Globe size={16} className="text-[#D4B982]" />
                  Facebook Pixel ID
                </Label>
                <Input
                  id="facebook_pixel"
                  value={globalSeo.facebook_pixel}
                  onChange={(e) => setGlobalSeo({ ...globalSeo, facebook_pixel: e.target.value })}
                  placeholder="XXXXXXXXXXXXXXXX"
                  data-testid="seo-facebook-pixel"
                />
                <p className="text-xs text-[#71717A] mt-1">
                  ID e Facebook Pixel për tracking të reklamave
                </p>
              </div>

              <div className="pt-4 border-t border-[#E4E4E7]">
                <Button
                  onClick={handleSaveGlobalSeo}
                  disabled={isSaving}
                  className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
                  data-testid="seo-save-tracking"
                >
                  {isSaving ? 'Duke ruajtur...' : (
                    <>
                      <Save size={18} className="mr-2" />
                      Ruaj Konfigurimet
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
